import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, addObjection, addFAQ } from '../services/apiService';
import { format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import SaveInquiryModal from '../components/SaveInquiryModal';
import { CheckCircle as CheckCircleIcon, ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon } from '@mui/icons-material';
import { Popover } from '@mui/material';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { formatFileSize } from '../utils/formatFileSize';
import CallInsightsTab from '../components/callInsights/CallInsightsTab';
import type { CallInsightsAnalysis } from '../components/adherence/types';

interface TranscriptEntry {
  id: string;
  text: string;
  speaker: 'customer' | 'salesperson';
  timestamp: string;
}

interface EntryFeedback {
  rating: 'up' | 'down';
  reason?: string;
  submittedAt?: string;
}

interface ConversationEntry {
  question: string;
  answer?: string;
  stored_answer?: string;  // Answer from vector store database
  category: string;
  relevance_score: number;
  timestamp: string;
  status: string;
  isProcessed?: boolean;
  feedback?: EntryFeedback;
}

interface SentimentEntry {
  transcript: string;
  speaker: string;
  dominant_emotion: string;
  confidence: number;
  emotions: Array<{
    emotion: string;
    confidence: number;
  }>;
  timestamp: string;
}

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface SessionDetailApiResponse {
  success: boolean;
  data?: SessionDetailData;
  message?: string;
  error?: string;
}

interface LandmineDetection {
  trigger: string;
  matched_text: string;
  category: string;
  priority: string;
  timestamp: string;
  speaker: string;
  feedback?: EntryFeedback;
}

interface CompetitorDetection {
  competitor: string;
  matched_text: string;
  confidence: number;
  timestamp: string;
  speaker: string;
  feedback?: EntryFeedback;
}

// Items within a role's tracking arrays
interface SalesPlaybookQuestionAsked {
  questionId: string;
  questionText: string;
  tags: string[];
  askedAt: string;
}

interface SalesPlaybookQuestionNote {
  questionId: string;
  questionText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookPitchUsed {
  pitchId: string;
  pitchText: string;
  usedAt: string;
}

interface SalesPlaybookPitchNote {
  pitchId: string;
  pitchText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookAdvancedUsed {
  advancedId: string;
  advancedText: string;
  usedAt: string;
}

interface SalesPlaybookAdvancedNote {
  advancedId: string;
  advancedText: string;
  note: string;
  timestamp: string;
}

// Each industry/role combination used during the session
interface SalesPlaybookRoleUsed {
  industry: string;
  industryDisplayName: string;
  role: string;
  roleDisplayName: string;
  questionsAsked: SalesPlaybookQuestionAsked[];
  questionNotes: SalesPlaybookQuestionNote[];
  pitchesUsed: SalesPlaybookPitchUsed[];
  pitchNotes: SalesPlaybookPitchNote[];
  advancedUsed: SalesPlaybookAdvancedUsed[];
  advancedNotes: SalesPlaybookAdvancedNote[];
}

interface SalesPlaybookUsage {
  // Current/last selection
  selectedIndustry?: string;
  selectedIndustryDisplayName?: string;
  selectedRole?: string;
  selectedRoleDisplayName?: string;
  totalQuestionsAvailable?: number;
  // All industry/role combinations used during the session
  rolesUsed?: SalesPlaybookRoleUsed[];
}

interface SessionDetailData {
  _id: string;
  sessionId: string;
  sessionName: string;
  userId: string;
  userEmail: string;
  callStartTime: string;
  callEndTime: string;
  callDuration: number;
  transcripts: TranscriptEntry[];
  detectedObjections: ConversationEntry[];
  detectedFAQs: ConversationEntry[];
  newInquiries: ConversationEntry[];
  sentimentAnalysis: {
    entries: SentimentEntry[];
    sessionSummary?: {
      total_transcripts: number;
      emotion_distribution: Record<string, number>;
      average_confidence: number;
      session_dominant_emotion: string;
      session_confidence: number;
    };
  };
  speechRateAnalysis: {
    alerts: SpeechRateAlert[];
    totalAlerts?: number;
    averageWordsPerMinute?: number;
    max_words?: number;
    time_window?: number;
  };
  landmineDetections?: LandmineDetection[];
  competitorDetections?: CompetitorDetection[];
  salesPlaybookUsage?: SalesPlaybookUsage;
  sessionFeedback?: Array<{
    comment: string;
    submittedAt: string;
    attachments: Array<{
      filename: string;
      originalName: string;
      cloudFrontUrl: string;
      fileSize: number;
      mimeType: string;
      uploadedAt: string;
    }>;
  }>;
  metadata: {
    roomName?: string;
    clientId?: string;
  };
  statistics: any;
  status: string;
  callInsights?: CallInsightsAnalysis;
}

// Read-only feedback indicator for admin session timeline
interface FeedbackBadgeProps {
  feedback?: { rating: 'up' | 'down'; reason?: string; submittedAt?: string };
}

const FeedbackBadge: React.FC<FeedbackBadgeProps> = ({ feedback }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!feedback?.rating) return null;

  const isUp = feedback.rating === 'up';

  return (
    <>
      <span
        onMouseEnter={e => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border cursor-default ${
          isUp
            ? 'text-green-700 border-green-300 bg-green-50'
            : 'text-red-700 border-red-300 bg-red-50'
        }`}
      >
        {isUp
          ? <ThumbUpIcon style={{ fontSize: 12 }} />
          : <ThumbDownIcon style={{ fontSize: 12 }} />}
        {isUp ? 'Helpful' : 'Not helpful'}
      </span>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
      >
        <div className="p-3 max-w-xs text-sm">
          <p className={`font-semibold mb-1 ${isUp ? 'text-green-700' : 'text-red-700'}`}>
            {isUp ? 'Marked helpful' : 'Marked not helpful'}
          </p>
          <p className="text-gray-600 text-xs">
            {feedback.reason || 'No reason provided'}
          </p>
          {feedback.submittedAt && (
            <p className="text-gray-400 text-xs mt-1">
              {format(new Date(feedback.submittedAt), 'PPp')}
            </p>
          )}
        </div>
      </Popover>
    </>
  );
};

const SessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const [session, setSession] = useState<SessionDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'call-insights' | 'timeline' | 'statistics' | 'salesperson-feedback'>('call-insights');
  const [scrollToTimestamp, setScrollToTimestamp] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [savingInquiry, setSavingInquiry] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState<ConversationEntry | null>(null);
  const [processedInquiries, setProcessedInquiries] = useState<Set<string>>(new Set());

  const fetchSessionDetail = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const response = await apiService.get(`/admin/sessions/${sessionId}`) as SessionDetailApiResponse;

      console.log('Session Detail API Response:', response); // Debug log

      // The response is the full response object from backend
      if (response?.success && response.data) {
        setSession(response.data);

        // Initialize processedInquiries from session data
        if (response.data.newInquiries) {
          const processed = new Set<string>();
          response.data.newInquiries.forEach((inq: ConversationEntry) => {
            if (inq.isProcessed) {
              processed.add(inq.question);
            }
          });
          setProcessedInquiries(processed);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch session detail:', err);
      setError(err.message || 'Failed to load session details');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetail();
    }
  }, [sessionId, fetchSessionDetail]);

  const handleNavigateToTimestamp = useCallback((ts: string) => {
    setActiveTab('timeline');
    setScrollToTimestamp(ts);
  }, []);

  useEffect(() => {
    if (activeTab !== 'timeline' || !scrollToTimestamp) return;

    const targetMs = new Date(scrollToTimestamp).getTime();
    if (isNaN(targetMs)) {
      setScrollToTimestamp(null);
      return;
    }

    // Give the timeline a tick to render before scanning for data-ts elements.
    const timerId = setTimeout(() => {
      const elements = document.querySelectorAll<HTMLElement>('[data-ts]');
      let closest: HTMLElement | null = null;
      let minDiff = Infinity;
      elements.forEach((el) => {
        const diff = Math.abs(new Date(el.dataset.ts!).getTime() - targetMs);
        if (diff < minDiff) {
          minDiff = diff;
          closest = el;
        }
      });

      if (closest) {
        (closest as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        (closest as HTMLElement).classList.add('ring-2', 'ring-blue-400', 'ring-offset-2', 'transition-all');
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          (closest as HTMLElement).classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2', 'transition-all');
        }, 3000);
      }

      setScrollToTimestamp(null);
    }, 50);

    return () => clearTimeout(timerId);
  }, [activeTab, scrollToTimestamp]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getCategoryColor = (category: string): string => {
    if (!category) return 'bg-gray-100 text-gray-800';
    if (category === 'FAQ') return 'bg-blue-100 text-blue-800';
    if (category === 'New Inquiry' || category === 'NEW_INQUIRY') return 'bg-green-100 text-green-800';
    if (category === 'Objection' || category === 'Hesitation' || category.includes('OBJECTION')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatAnswerText = (text: string): React.ReactElement => {
    // Only split by actual newlines to preserve text integrity
    const lines = text.split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-1">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          // Check if it's a bullet point or numbered item at the start of a line
          if (trimmedLine.match(/^[•\-\*]/) || trimmedLine.match(/^\d+\./)) {
            return (
              <div key={index} className="flex items-start gap-2">
                <span className="text-gray-500 mt-0.5">•</span>
                <span className="flex-1">{trimmedLine.replace(/^[•\-\*\d+\.]\s*/, '')}</span>
              </div>
            );
          } else if (trimmedLine) {
            return <div key={index}>{trimmedLine}</div>;
          }
          return null;
        })}
      </div>
    );
  };

  const renderTimelineView = () => {
    if (!session) return null;

    // Combine all events into a timeline
    const timelineEvents: any[] = [];

    // Add transcripts
    session.transcripts.forEach(transcript => {
      timelineEvents.push({
        type: 'transcript',
        timestamp: transcript.timestamp,
        data: transcript
      });
    });

    // Add detected questions/objections
    [...session.detectedObjections, ...session.detectedFAQs, ...session.newInquiries].forEach(entry => {
      timelineEvents.push({
        type: 'detection',
        timestamp: entry.timestamp,
        data: entry
      });
    });

    // Add speech rate alerts
    session.speechRateAnalysis.alerts.forEach(alert => {
      timelineEvents.push({
        type: 'speechAlert',
        timestamp: alert.timestamp,
        data: alert
      });
    });

    // Add landmine detections
    if (session.landmineDetections) {
      session.landmineDetections.forEach(detection => {
        timelineEvents.push({
          type: 'landmine',
          timestamp: detection.timestamp,
          data: detection
        });
      });
    }

    // Add competitor detections
    if (session.competitorDetections) {
      session.competitorDetections.forEach(detection => {
        timelineEvents.push({
          type: 'competitor',
          timestamp: detection.timestamp,
          data: detection
        });
      });
    }

    // Sort by timestamp
    timelineEvents.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return (
      <div className="space-y-4">
        {timelineEvents.map((event, index) => (
          <div key={index} data-ts={event.timestamp} className="flex gap-4">
            <div className="flex-shrink-0 w-24 text-xs text-gray-500">
              {format(new Date(event.timestamp), 'HH:mm:ss')}
            </div>
            <div className="flex-grow">
              {event.type === 'transcript' && (
                <div className={`p-3 rounded-lg ${
                  event.data.speaker === 'customer' ? 'bg-blue-50' : 'bg-green-50'
                }`}>
                  <div className="text-xs font-semibold mb-1">
                    {event.data.speaker === 'customer' ? '👤 Customer' : '💼 Salesperson'}
                  </div>
                  <div className="text-sm">{event.data.text}</div>
                </div>
              )}
              
              {event.type === 'detection' && (
                <div className="p-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-400">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(event.data.category)}`}>
                        {event.data.category === 'New Inquiry' || event.data.category === 'NEW_INQUIRY' ? 'New Objection/FAQ' : event.data.category}
                      </span>
                      <span className="text-xs text-gray-500">
                        Relevance: {(event.data.relevance_score * 100).toFixed(0)}%
                      </span>
                      <FeedbackBadge feedback={event.data.feedback} />
                    </div>
                    {/* Only show buttons for admin users */}
                    {isAdmin && (event.data.category === 'New Inquiry' || event.data.category === 'NEW_INQUIRY') && !event.data.isProcessed && !processedInquiries.has(event.data.question) && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleMarkAsProcessed(event.data)}
                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
                          disabled={savingInquiry === event.data.question}
                        >
                          Mark as Processed
                        </button>
                        <button
                          onClick={() => setShowSaveModal(event.data)}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                          disabled={savingInquiry === event.data.question}
                        >
                          Add/Modify Database
                        </button>
                      </div>
                    )}
                    {(event.data.isProcessed || processedInquiries.has(event.data.question)) && (
                      <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                        ✓ Processed
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium">{event.data.question}</div>

                  {/* Display Stored Answer and/or AI Answer */}
                  {(event.data.stored_answer || event.data.answer) && (
                    <div className="mt-2 text-sm text-gray-700">
                      {/* Stored Answer (from database) */}
                      {event.data.stored_answer && (
                        <div className="mb-2">
                          <div className="font-medium mb-1 font-semibold flex items-center gap-2">
                            <span>📚 Stored Answer (Database):</span>
                          </div>
                          <div className="bg-blue-50 p-2 rounded border border-blue-200">
                            {formatAnswerText(event.data.stored_answer)}
                          </div>
                        </div>
                      )}

                      {/* Divider if both answers exist */}
                      {event.data.stored_answer && event.data.answer && (
                        <div className="my-3 border-t border-gray-300"></div>
                      )}

                      {/* AI Answer */}
                      {event.data.answer && (
                        <div>
                          <div className="font-medium mb-1 font-semibold flex items-center gap-2">
                            <span>🤖 AI Generated Answer:</span>
                          </div>
                          <div className="bg-green-50 p-2 rounded border border-green-200">
                            {formatAnswerText(event.data.answer)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {event.type === 'speechAlert' && (
                <div className="p-3 rounded-lg bg-red-50 border-l-4 border-red-400">
                  <div className="text-xs font-semibold mb-1">⚠️ Speech Rate Alert</div>
                  <div className="text-sm">
                    Speaking too fast: {event.data.words_per_minute} words/minute
                    ({event.data.total_words} words in {session.speechRateAnalysis?.time_window || 30}s)
                  </div>
                </div>
              )}

              {event.type === 'landmine' && (
                <div className={`p-3 rounded-lg border-l-4 ${
                  event.data.priority === 'high' ? 'bg-red-50 border-red-500' :
                  event.data.priority === 'medium' ? 'bg-orange-50 border-orange-400' :
                  'bg-yellow-50 border-yellow-400'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold">⚠️ Landmine Detected</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      event.data.priority === 'high' ? 'bg-red-100 text-red-800' :
                      event.data.priority === 'medium' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {event.data.priority} priority
                    </span>
                    <FeedbackBadge feedback={event.data.feedback} />
                  </div>
                  <div className="text-sm font-medium">Trigger: "{event.data.trigger}"</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Category: {event.data.category} | Speaker: {event.data.speaker}
                  </div>
                </div>
              )}

              {event.type === 'competitor' && (
                <div className="p-3 rounded-lg bg-purple-50 border-l-4 border-purple-400">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold">🎯 Competitor Mentioned</span>
                    <FeedbackBadge feedback={event.data.feedback} />
                  </div>
                  <div className="text-sm font-medium">{event.data.competitor}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Confidence: {(event.data.confidence * 100).toFixed(0)}% | Speaker: {event.data.speaker}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };


  const renderSalespersonFeedbackView = () => {
    if (!session) return null;

    const entries = session.sessionFeedback ?? [];

    if (entries.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <span className="text-4xl mb-3">💬</span>
          <p className="text-lg font-medium">No session feedback submitted</p>
          <p className="text-sm mt-1">The sales rep did not submit any general feedback for this session.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{entries.length} feedback {entries.length === 1 ? 'entry' : 'entries'}</p>
        {entries.map((entry, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4">
            <div className="mb-2">
              <span className="text-xs text-gray-400">
                {format(new Date(entry.submittedAt), 'MMM dd, yyyy HH:mm')}
              </span>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.comment}</p>
            {entry.attachments?.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-medium text-gray-500 mb-1">Attachments ({entry.attachments.length})</p>
                {entry.attachments.map((file, fileIdx) => (
                  <a
                    key={fileIdx}
                    href={file.cloudFrontUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <span>📎</span>
                    <span className="truncate">{file.originalName}</span>
                    <span className="text-xs text-gray-400 shrink-0">({formatFileSize(file.fileSize)})</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStatisticsView = () => {
    if (!session) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Conversation Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Transcripts</span>
              <span className="font-semibold">{session.statistics?.totalTranscripts || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Customer Transcripts</span>
              <span className="font-semibold">{session.statistics?.customerTranscripts || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Salesperson Transcripts</span>
              <span className="font-semibold">{session.statistics?.salespersonTranscripts || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Questions & Objections</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Objections</span>
              <span className="font-semibold text-red-600">{session.statistics?.totalObjections || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total FAQs</span>
              <span className="font-semibold text-blue-600">{session.statistics?.totalFAQs || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">New Objection/FAQ</span>
              <span className="font-semibold text-green-600">{session.statistics?.totalNewInquiries || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Response Rate</span>
              <span className="font-semibold">{session.statistics?.responseRate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Detections</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Landmine Alerts</span>
              <span className="font-semibold text-orange-600">
                {session.statistics?.totalLandmineDetections || session.landmineDetections?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Competitor Mentions</span>
              <span className="font-semibold text-purple-600">
                {session.statistics?.totalCompetitorMentions || session.competitorDetections?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Speech Rate Alerts</span>
              <span className="font-semibold text-red-600">
                {session.speechRateAnalysis?.totalAlerts || session.speechRateAnalysis?.alerts?.length || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Sales Playbook</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Industry</span>
              <span className="font-semibold text-blue-600">
                {session.salesPlaybookUsage?.selectedIndustryDisplayName || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Roles Used</span>
              <span className="font-semibold text-purple-600">
                {session.salesPlaybookUsage?.rolesUsed?.length || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Questions Asked</span>
              <span className="font-semibold text-green-600">
                {session.salesPlaybookUsage?.rolesUsed?.reduce((sum, r) => sum + (r.questionsAsked?.length || 0), 0) || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Notes Taken</span>
              <span className="font-semibold text-yellow-600">
                {session.salesPlaybookUsage?.rolesUsed?.reduce((sum, r) =>
                  sum + (r.questionNotes?.length || 0) + (r.pitchNotes?.length || 0) + (r.advancedNotes?.length || 0), 0) || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Call Timing</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Duration</span>
              <span className="font-semibold text-blue-600">
                {formatDuration(session.callDuration)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Started</span>
              <span className="text-gray-700">
                {format(new Date(session.callStartTime), 'HH:mm:ss')}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Ended</span>
              <span className="text-gray-700">
                {format(new Date(session.callEndTime), 'HH:mm:ss')}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200">
              <span className="text-gray-600">Room Name</span>
              <span className="font-semibold">{session.metadata?.roomName || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                session.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {session.status}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const handleMarkAsProcessed = async (inquiry: ConversationEntry) => {
    // Confirm with user
    const confirmed = window.confirm(
      'Are you sure you want to mark this inquiry as processed WITHOUT adding it to the knowledge base?\n\n' +
      'This will hide the inquiry from your unprocessed list, but it will NOT be saved to the FAQs or Objections database.'
    );

    if (!confirmed) {
      return;
    }

    try {
      // Mark as processed in the backend
      await apiService.put(`/admin/sessions/${sessionId}/inquiry-processed`, {
        question: inquiry.question
      });

      // Update UI
      setProcessedInquiries(prev => new Set([...prev, inquiry.question]));

      // Update session data
      if (session) {
        const updatedSession = { ...session };
        updatedSession.newInquiries = updatedSession.newInquiries.map(inq =>
          inq.question === inquiry.question ? { ...inq, isProcessed: true } : inq
        );
        setSession(updatedSession);
      }

      // Show success message
      setSuccess('Inquiry marked as processed');
      setTimeout(() => setSuccess(null), 8000); // Increased to 8 seconds

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Failed to mark as processed:', err);
      setError('Failed to mark inquiry as processed');
    }
  };

  const handleSaveNewInquiry = async (inquiry: ConversationEntry, type: 'objection' | 'faq', editedData: { question: string; answer: string; category: string }) => {
    try {
      setSavingInquiry(inquiry.question);

      // First, save to the dictionary (objection or FAQ)
      if (type === 'objection') {
        // Pass: question, answer, category (for topic), isOriginal=true, parentId=null
        await addObjection(editedData.question, editedData.answer, editedData.category, true, null);
      } else {
        // FAQs: question, answer, category (for topic)
        await addFAQ(editedData.question, editedData.answer, editedData.category);
      }

      // Show success message for saving to database
      setError(null);  // Clear any previous errors
      setSuccess(`Successfully added to ${type === 'objection' ? 'objections' : 'FAQs'} database`);
      setTimeout(() => setSuccess(null), 10000); // Increased from 5 seconds to 10 seconds

      // Then, try to mark as processed in the backend
      try {
        console.log('[SessionDetail] Attempting to mark inquiry as processed');
        console.log('[SessionDetail] Session ID:', sessionId);
        console.log('[SessionDetail] Question being sent:', inquiry.question);

        const response = await apiService.put(`/admin/sessions/${sessionId}/inquiry-processed`, {
          question: inquiry.question
        });

        console.log('[SessionDetail] Mark as processed response:', response);

        // Only mark as processed in UI if backend call succeeds
        setProcessedInquiries(prev => new Set([...prev, inquiry.question]));

        // Update session data to mark as processed
        if (session) {
          const updatedSession = { ...session };
          updatedSession.newInquiries = updatedSession.newInquiries.map(inq =>
            inq.question === inquiry.question ? { ...inq, isProcessed: true } : inq
          );
          setSession(updatedSession);
        }

        console.log('[SessionDetail] Successfully marked as processed in UI');
      } catch (err: any) {
        console.error('[SessionDetail] Failed to persist processed state:', err);
        console.error('[SessionDetail] Error details:', {
          message: err.message,
          response: err.response,
          data: err.response?.data
        });
        // Update success message with warning (but don't fail the whole operation)
        setSuccess(prev => prev + ' (⚠️ Note: Could not mark as processed in session)');
      }

      setShowSaveModal(null);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Failed to save inquiry:', error);
      alert(error.message || 'Failed to save inquiry to database');
    } finally {
      setSavingInquiry(null);
    }
  };

  return (
    <MainLayout title={session ? session.sessionName : 'Session Details'}>
      <div className="p-2">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="ml-3 text-gray-500">Loading session details...</p>
        </div>
      ) : error || !session ? (
        <div>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Session not found'}
          </div>
          <button
            onClick={() => navigate('/sessions')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Sessions
          </button>
        </div>
      ) : (
        <>
      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="text-green-600 mr-2" style={{ fontSize: '1.25rem' }} />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-3">
        <button
          onClick={() => navigate('/sessions')}
          className="text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-2"
        >
          ← Back to Sessions
        </button>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-start justify-between">
            <h1 className="text-2xl font-bold text-gray-900">{session.sessionName}</h1>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
              🤖 Bot Recording
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">User:</span> {session.userEmail}
            </div>
            <div>
              <span className="font-medium">Duration:</span>{' '}
              <span className="text-blue-600 font-semibold">{formatDuration(session.callDuration)}</span>
            </div>
            <div>
              <span className="font-medium">Date:</span> {format(new Date(session.callStartTime), 'MMM dd, yyyy HH:mm')}
            </div>
            <div>
              <span className="font-medium">Room:</span> {session.metadata?.roomName || '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {[
              { id: 'call-insights', label: 'Call Insights', icon: '🎯' },
              { id: 'timeline', label: 'Timeline', icon: '📅' },
              { id: 'statistics', label: 'Statistics', icon: '📊' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 mr-8 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {/* <span className="mr-2">{tab.icon}</span> */}
                {tab.label}
              </button>
            ))}
            {/* Right-aligned Salesperson Feedback */}
            <button
              onClick={() => setActiveTab('salesperson-feedback')}
              className={`py-2 px-1 ml-auto border-b-2 font-medium text-sm ${
                activeTab === 'salesperson-feedback'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {/* <span className="mr-2">💬</span> */}
              Salesperson Feedback
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-4">
        {activeTab === 'call-insights' && (
          <CallInsightsTab
            sessionId={session.sessionId}
            callInsights={session.callInsights}
            salesPlaybookUsage={session.salesPlaybookUsage}
            speechRateAlerts={session.speechRateAnalysis?.alerts || []}
            isAdmin={isAdmin}
            onAnalysisUpdated={() => fetchSessionDetail(true)}
            onNavigateToTimestamp={handleNavigateToTimestamp}
          />
        )}
        {activeTab === 'timeline' && renderTimelineView()}
        {activeTab === 'statistics' && renderStatisticsView()}
        {activeTab === 'salesperson-feedback' && renderSalespersonFeedbackView()}
      </div>

      {/* Save New Inquiry Modal */}
      {showSaveModal && (
        <SaveInquiryModal
          inquiry={showSaveModal}
          onClose={() => setShowSaveModal(null)}
          onSave={handleSaveNewInquiry}
          onMarkAsProcessed={handleMarkAsProcessed}
          isSaving={savingInquiry === showSaveModal.question}
        />
      )}
        </>
      )}
      </div>
    </MainLayout>
  );
};

export default SessionDetail;